import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { get, fn, array } from '@ember/helper';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { or, and } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import {
  HdsPageHeader,
  HdsTable,
  HdsDropdown,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Time,
  Flex,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import { rbacRoles } from 'core/helpers';
import { INVITATION_RESENT } from 'core/utils/consts/analytics-events/platform';
import Resend from 'hcp/components/iam/invitations/modals/resend';
import Revoke from 'hcp/components/iam/invitations/modals/revoke';

/**
 * `model` has the list of HCP organization invites and inviters.
 * @argument model
 * @type {Object}
 */
/**
 *
 * `PageInvitesList` lists the HCP Invites.
 *
 *
 * ```
 * <Page::Invites::List @model={{model}} />
 * ```
 *
 * @class PageInvitesList
 *
 */
export default class PageInvitesList extends Component {
  @service analytics;
  @service api;
  @service currentUser;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked showRevokeModal = false;
  @tracked inviterMap = {};
  @tracked invitesToRevoke = null;
  @tracked showResendModal = false;
  @tracked inviteToResend = null;

  resendInvite = task(async (evt) => {
    evt.preventDefault();

    this.analytics.trackEvent(INVITATION_RESENT, {
      invitation: this.inviteToResend,
    });

    const { deleted } = await this.deleteInvite.perform();
    const { created } = deleted ? await this.createInvite.perform() : {};

    created ? this.handleSuccess() : this.handleError();
  });

  deleteInvite = task(async () => {
    try {
      await this.api.invitation.invitationsServiceDeleteOrganizationInvitation(
        this.inviteToResend.organizationId,
        this.inviteToResend.id
      );
      return {
        deleted: true,
      };
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      return {
        deleted: false,
      };
    }
  });

  createInvite = task(async () => {
    try {
      await this.api.invitation.invitationsServiceCreateOrganizationInvitations(
        this.inviteToResend.organizationId,
        {
          invitations: [
            {
              inviteeEmail: this.inviteToResend.inviteeEmail,
              role: this.inviteToResend.role,
            },
          ],
        }
      );
      return {
        created: true,
      };
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      return {
        created: false,
      };
    }
  });

  buildInviterMap = () => {
    let { userPrincipals = [] } = this.args.model;

    this.inviterMap = userPrincipals.reduce((map, user) => {
      map[user.id] = user;
      return map;
    }, {});

    return this;
  };

  confirmRevokeInvites = (invites) => {
    this.showRevokeModal = true;
    this.invitesToRevoke = invites;
  };

  confirmResendInvite = (invite) => {
    this.showResendModal = true;
    this.inviteToResend = invite[0];
  };

  onRevokeInvitesSuccess = ({ invites }) => {
    this.resetRevokeInvites();

    // We should use the router service for this once this PR is merged.
    // via: https://github.com/emberjs/rfcs/issues/592
    let route = getOwner(this).lookup(`route:${this.router.currentRouteName}`);

    this.flashMessages.success(
      this.intl.t(`iam.invitations.modals.revoke.success-title`),
      {
        content: this.intl.t(`iam.invitations.modals.revoke.success-body`, {
          user: invites[0].inviteeEmail,
          htmlSafe: true,
        }),
      }
    );
    return route.refresh();
  };

  handleError = () => {
    this.flashMessages.error(
      this.intl.t('iam.invitations.modals.resend.error.title'),
      {
        content: this.intl.t('iam.invitations.modals.resend.error.body', {
          inviteeEmail: this.inviteToResend.inviteeEmail,
          htmlSafe: true,
        }),
      }
    );
    this.resetResendInvite();
  };

  handleSuccess = () => {
    this.flashMessages.success(
      this.intl.t('iam.invitations.modals.resend.success.title'),
      {
        content: this.intl.t('iam.invitations.modals.resend.success.body', {
          inviteeEmail: this.inviteToResend.inviteeEmail,
          htmlSafe: true,
        }),
      }
    );
    this.resetResendInvite();
    const route = getOwner(this).lookup(
      `route:${this.router.currentRouteName}`
    );
    if (route) {
      return route.refresh();
    }
  };

  resetResendInvite = () => {
    this.showResendModal = false;
    this.inviteToResend = null;
  };

  resetRevokeInvites = () => {
    this.showRevokeModal = false;
    this.invitesToRevoke = null;
  };

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="hourglass" />
          <PH.Title>
            {{t "components.page.access-control.invites.list.title"}}
          </PH.Title>
        </HdsPageHeader>
      </:header>
      <:body>
        <section>
          {{#if @model.invitations.length}}
            <HdsTable
              @caption={{t "components.page.access-control.invites.list.title"}}
              @valign="middle"
              {{didInsert this.buildInviterMap}}
            >
              <:head as |H|>
                <H.Tr>
                  <H.Th>
                    {{t
                      "components.page.access-control.invites.list.table.headers.email"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.access-control.invites.list.table.headers.role"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.access-control.invites.list.table.headers.invited"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.access-control.invites.list.table.headers.inviter"
                    }}
                  </H.Th>
                  {{#if (can "delete invitations")}}
                    <H.Th @width="85px">
                      {{t
                        "components.page.access-control.invites.list.table.headers.actions"
                      }}
                    </H.Th>
                  {{/if}}
                </H.Tr>
              </:head>
              <:body as |B|>
                {{#each @model.invitations key="id" as |invitation|}}
                  <B.Tr data-test-invitation-item>
                    <B.Td data-test-invitation-item-email>
                      {{invitation.inviteeEmail}}
                    </B.Td>
                    <B.Td data-test-invitation-item-role>
                      {{#if invitation.role}}
                        {{get (rbacRoles invitation.role) "label"}}
                      {{else}}
                        —
                      {{/if}}
                    </B.Td>
                    <B.Td data-test-invitation-item-invited>
                      <Time @date={{invitation.createdAt}} />
                    </B.Td>
                    <B.Td data-test-invitation-inviter-name>
                      {{#let
                        (get
                          (get
                            this.inviterMap invitation.inviterUserPrincipalId
                          )
                          "fullName"
                        )
                        as |fullName|
                      }} {{or fullName "—"}} {{/let}}
                    </B.Td>
                    {{#if (can "delete invitations")}}
                      <B.Td>
                        <Flex @justifyContent="end">
                          <HdsDropdown as |dd|>
                            <dd.ToggleIcon
                              @icon="more-horizontal"
                              @text={{t "components.form.more"}}
                              @hasChevron={{false}}
                              data-test-user-item-menu
                              data-test-more-menu-trigger
                            />
                            {{#if
                              (and
                                (can "delete invitations")
                                (can
                                  "create invitation"
                                  undefined
                                  ssoEnabled=@model.ssoEnabled
                                )
                              )
                            }}
                              <dd.Interactive
                                @color="action"
                                @icon="send"
                                @text={{t
                                  "components.page.access-control.invites.list.table.options.resend"
                                }}
                                data-test-resend-invitations-button
                                {{on
                                  "click"
                                  (fn
                                    this.confirmResendInvite (array invitation)
                                  )
                                }}
                              />
                            {{/if}}
                            <dd.Interactive
                              @color="critical"
                              @icon="minus-circle"
                              @text={{t
                                "components.page.access-control.invites.list.table.options.revoke"
                              }}
                              data-test-revoke-invitations-button
                              {{on
                                "click"
                                (fn
                                  this.confirmRevokeInvites (array invitation)
                                )
                              }}
                            />
                          </HdsDropdown>
                        </Flex>
                      </B.Td>
                    {{/if}}
                  </B.Tr>
                {{/each}}
              </:body>
            </HdsTable>
          {{else}}
            <HdsApplicationState {{boxPadding "2xl 0"}} as |A|>
              <A.Header
                @title={{t
                  "components.page.access-control.invites.list.empty.header"
                }}
              />
              <A.Body
                @text={{t
                  "components.page.access-control.invites.list.empty.message"
                }}
                data-test-invitation-empty-message
              />
              {{#if
                (can "create invitation" undefined ssoEnabled=@model.ssoEnabled)
              }}
                <A.Footer as |F|>
                  <F.LinkStandalone
                    @icon="arrow-right"
                    @iconPosition="trailing"
                    @route="cloud.access-control.users.invite"
                    @text={{t
                      "components.page.access-control.users.invite.title"
                    }}
                    data-test-users-create
                  />
                </A.Footer>
              {{/if}}
            </HdsApplicationState>
          {{/if}}
        </section>

        <Resend
          @invite={{this.inviteToResend}}
          @isActive={{this.showResendModal}}
          @onClose={{this.resetResendInvite}}
          @onSubmit={{this.resendInvite.perform}}
        />

        <Revoke
          @isActive={{this.showRevokeModal}}
          @organization={{@model.organization}}
          @onCancel={{this.resetRevokeInvites}}
          @onSuccess={{this.onRevokeInvitesSuccess}}
          @invites={{this.invitesToRevoke}}
        />
      </:body>
    </CoreLayoutMain>
  </template>
}
